import {
  addDays,
  buildCraving,
  buildDayTotal,
  buildEnsureCigarettesCategory,
  buildRemoveLog,
  buildRestoreLog,
  buildSavePlan,
  buildSetTriggerPlan,
  buildTap,
  formatPeso,
  limitOn,
  localDate,
  newId,
  phaseOn,
  seededId,
  singletonId,
  type BuildCtx,
  type DomainEvent,
  type LocalDate,
  type Mutation,
  type PlanDraft,
  type QuitPhase,
  type Trigger,
} from '@tahi/core';
import {
  QUIT_DASHBOARD_ID,
  QUIT_DASHBOARD_TILES,
} from '../../modules/quit_smoking/dashboardDefaults';
import { nextPosition, TODAY } from '../../tiles/layout';
import { makeMutation, monotonicNow } from '../mutation';
import * as repo from '../repos/quit';
import { getUserSettings, listAllCategories } from '../repos/spending';
import { listTiles } from '../repos/tiles';
import { runCommand, type CommandEnv } from './dispatch';

export type QuitCommandResult<T = object> = ({ ok: true } & T) | { ok: false; error: string };

/** Seed markers in user_settings.seeds (bump only if existing users should get the additions). */
export const QUIT_SEED = 'quit_smoking.v1';
export const QUIT_CATEGORY_SEED = 'quit_smoking.cigarettes.v1';

export interface LoggedResult {
  id: string;
  date: LocalDate;
  /** The day's count after the change (spec §7). */
  count: number;
  limit: number | null;
  tomorrowLimit: number | null;
  phase: QuitPhase;
  /** What this cigarette cost (0 without a price). */
  costMinor: number;
}

function ctx(env: CommandEnv): BuildCtx {
  return { now: monotonicNow(), tz: env.tz, newId };
}

let queue: Promise<unknown> = Promise.resolve();

/** Quit commands read a day before writing it; one at a time, so two quick taps never read the same count. */
function serial<T>(fn: () => Promise<T>): Promise<T> {
  const run = queue.then(fn, fn);
  queue = run.catch(() => undefined);
  return run;
}

/**
 * A smoke log write plus the rules it sets off: the day's one Cigarettes expense (when Spending is
 * usable) and Budget through it. The connection toast ("Logged · Quit Smoking ┄ Spending ₱12.00
 * ┄ Budget") shows only when the change reached another module.
 */
async function commitSmoke(
  env: CommandEnv,
  mutation: Mutation,
  event: DomainEvent,
  spendingDetail: string | null,
): Promise<void> {
  await runCommand(env, {
    mutations: [mutation],
    event,
    ...(spendingDetail
      ? { toast: { headline: 'Logged', details: { spending: spendingDetail } } }
      : {}),
  });
}

const money = (minor: number) => formatPeso(minor, { cents: 'always' });

export function savePlan(env: CommandEnv, draft: PlanDraft): Promise<QuitCommandResult> {
  return serial(async () => {
    const existing = await repo.getPlan(env.db, env.userId);
    const r = buildSavePlan(env.userId, existing, draft, ctx(env));
    if (!r.ok) return r;
    if (r.mutation) await runCommand(env, { mutations: [r.mutation] });
    return { ok: true };
  });
}

/** +1: one cigarette now. */
export function logCigarette(
  env: CommandEnv,
  input: { trigger?: Trigger | null } = {},
): Promise<QuitCommandResult<LoggedResult>> {
  return serial(async () => {
    const plan = await repo.getPlan(env.db, env.userId);
    if (!plan) return { ok: false, error: 'no_plan' };
    const c = ctx(env);
    const date = localDate(c.now, env.tz);
    const r = buildTap(plan, await repo.listDayLogs(env.db, env.userId, date), input, c);
    if (!r.ok) return r;
    await commitSmoke(
      env,
      r.mutation,
      r.event,
      r.log.costMinor > 0 ? money(r.log.costMinor) : null,
    );
    return {
      ok: true,
      id: r.log.id,
      date,
      count: r.tally.count,
      limit: limitOn(plan.taper, date),
      tomorrowLimit: limitOn(plan.taper, addDays(date, 1)),
      phase: phaseOn(plan.taper, date),
      costMinor: r.log.costMinor,
    };
  });
}

/** "I smoked N on this day": today's total, or a backfilled past day. Replaces the taps before it. */
export function setDayTotal(
  env: CommandEnv,
  date: LocalDate,
  count: number,
): Promise<QuitCommandResult<{ id: string; count: number }>> {
  return serial(async () => {
    const plan = await repo.getPlan(env.db, env.userId);
    if (!plan) return { ok: false, error: 'no_plan' };
    const c = ctx(env);
    const dayLogs = await repo.listDayLogs(env.db, env.userId, date);
    const r = buildDayTotal(plan, dayLogs, { date, count }, c);
    if (!r.ok) return r;
    const when = date === localDate(c.now, env.tz) ? 'today' : 'that day';
    await commitSmoke(
      env,
      r.mutation,
      r.event,
      r.tally.costMinor > 0 ? `${money(r.tally.costMinor)} ${when}` : null,
    );
    return { ok: true, id: r.log.id, count: r.tally.count };
  });
}

/** Undo of a +1 or of a written total, or deleting a log from a day. */
export function removeSmokeLog(
  env: CommandEnv,
  id: string,
): Promise<QuitCommandResult<{ date: LocalDate; count: number }>> {
  return serial(async () => {
    const target = await repo.getSmokeLog(env.db, env.userId, id);
    if (!target || target.deletedAt) return { ok: false, error: 'not_found' };
    const r = buildRemoveLog(await repo.listDayLogs(env.db, env.userId, target.date), id, ctx(env));
    if (!r.ok) return r;
    await commitSmoke(env, r.mutation, r.event, null);
    return { ok: true, date: target.date, count: r.tally.count };
  });
}

/** Undo of a delete. */
export function restoreSmokeLog(
  env: CommandEnv,
  id: string,
): Promise<QuitCommandResult<{ date: LocalDate; count: number }>> {
  return serial(async () => {
    const target = await repo.getSmokeLog(env.db, env.userId, id);
    if (!target) return { ok: false, error: 'not_found' };
    const dayLogs = await repo.listDayLogs(env.db, env.userId, target.date, {
      includeDeleted: true,
    });
    const r = buildRestoreLog(dayLogs, id, ctx(env));
    if (!r.ok) return r;
    await commitSmoke(env, r.mutation, r.event, null);
    return { ok: true, date: target.date, count: r.tally.count };
  });
}

export async function logCraving(
  env: CommandEnv,
  input: { resisted: boolean; trigger: Trigger | null; delayUsed: boolean },
): Promise<QuitCommandResult<{ id: string }>> {
  const r = buildCraving(input, ctx(env));
  if (!r.ok) return r;
  await runCommand(env, { mutations: [r.mutation] });
  return { ok: true, id: r.craving.id };
}

/** Undo of a craving log. */
export async function removeCraving(env: CommandEnv, id: string): Promise<void> {
  await runCommand(env, {
    mutations: [makeMutation('cravings', 'delete', id, undefined, 'quit_smoking')],
  });
}

export function setTriggerPlan(
  env: CommandEnv,
  trigger: Trigger,
  text: string,
): Promise<QuitCommandResult> {
  return serial(async () => {
    const plan = await repo.getPlan(env.db, env.userId);
    if (!plan) return { ok: false, error: 'no_plan' };
    const r = buildSetTriggerPlan(plan, trigger, text, ctx(env));
    if (!r.ok) return r;
    await runCommand(env, { mutations: [r.mutation] });
    return { ok: true };
  });
}

/**
 * Privacy (spec: "can be hidden from the Today screen"): hides or shows every Quit Smoking tile on
 * Today. Showing with none placed puts the default quit.today tile back (an insert restores a removed one).
 */
export async function setQuitOnToday(env: CommandEnv, visible: boolean): Promise<void> {
  const quitTiles = await repo.listQuitTodayTiles(env.db);
  const mutations: Mutation[] = quitTiles
    .filter((t) => Boolean(t.hidden) === visible)
    .map((t) => makeMutation('tiles', 'update', t.id, { hidden: !visible }, 'quit_smoking'));
  if (visible && quitTiles.length === 0) {
    const all = await listTiles(env.db, TODAY);
    mutations.push(
      makeMutation(
        'tiles',
        'insert',
        seededId(env.userId, 'tile', TODAY, 'quit.today'),
        {
          dashboardId: TODAY,
          moduleId: 'quit_smoking',
          type: 'quit.today',
          position: nextPosition(all),
          size: 'wide',
          hidden: false,
          config: {},
        },
        'quit_smoking',
      ),
    );
  }
  if (mutations.length > 0) await runCommand(env, { mutations });
}

function seedMarker(
  env: CommandEnv,
  seeds: Record<string, string> | null,
  key: string,
  now: string,
): Mutation {
  const settingsId = singletonId(env.userId, 'user_settings');
  const next = { ...seeds, [key]: now };
  return seeds
    ? makeMutation('user_settings', 'update', settingsId, { seeds: next }, 'quit_smoking', now)
    : {
        ...makeMutation(
          'user_settings',
          'insert',
          settingsId,
          { weekStart: 1, seeds: next },
          'quit_smoking',
          now,
        ),
        ifAbsent: true as const,
      };
}

/** The Quit Smoking dashboard tiles, once per user; seeded ids + ifAbsent never reset a tile. */
export async function seedQuitDashboard(env: CommandEnv): Promise<void> {
  const settings = await getUserSettings(env.db, env.userId);
  if (settings?.seeds[QUIT_SEED]) return;
  const c = ctx(env);
  const rows = await listTiles(env.db, QUIT_DASHBOARD_ID, { includeDeleted: true });
  const have = new Set(rows.map((r) => r.type));
  const tiles: Mutation[] = QUIT_DASHBOARD_TILES.flatMap((t, i): Mutation[] =>
    have.has(t.type)
      ? []
      : [
          {
            ...makeMutation(
              'tiles',
              'insert',
              seededId(env.userId, 'tile', QUIT_DASHBOARD_ID, t.type),
              {
                dashboardId: QUIT_DASHBOARD_ID,
                moduleId: 'quit_smoking',
                type: t.type,
                position: i + 1,
                size: t.size,
                hidden: false,
                config: {},
              },
              'quit_smoking',
              c.now,
            ),
            ifAbsent: true as const,
          },
        ],
  );
  await runCommand(env, {
    mutations: [...tiles, seedMarker(env, settings?.seeds ?? null, QUIT_SEED, c.now)],
  });
}

/**
 * The Cigarettes category (v0.1.1 note): created when Quit Smoking is on, on its seeded id;
 * restored with a plain insert when the v0.1.1 cleanup (or the user, earlier) deleted it. Once per
 * user, so a Cigarettes category the user deletes later stays deleted.
 */
export async function ensureCigarettesCategory(env: CommandEnv): Promise<void> {
  const settings = await getUserSettings(env.db, env.userId);
  if (settings?.seeds[QUIT_CATEGORY_SEED]) return;
  const c = ctx(env);
  const ms = buildEnsureCigarettesCategory(
    env.userId,
    await listAllCategories(env.db, env.userId),
    c,
  );
  await runCommand(env, {
    mutations: [...ms, seedMarker(env, settings?.seeds ?? null, QUIT_CATEGORY_SEED, c.now)],
  });
}

/**
 * Waits for this session's first sync (so a Cigarettes row deleted on the server is here before
 * deciding between seed and restore), then ensures the category. Without a sync client (tests),
 * runs now.
 */
export function ensureCigarettesCategoryAfterSync(env: CommandEnv): Promise<void> {
  const status = env.sync.status;
  const run = () =>
    ensureCigarettesCategory(env).catch((err: unknown) => {
      console.warn('Tahi: creating the Cigarettes category failed', err);
    });
  if (!status || status.get().lastSyncedAt !== null) return run();
  return new Promise<void>((resolve) => {
    const unsubscribe = status.subscribe(() => {
      if (status.get().lastSyncedAt === null) return;
      unsubscribe();
      void run().then(resolve);
    });
  });
}

/** The Quit Smoking usable hook (register.tsx): dashboard tiles now, the category after sync. */
export async function onQuitUsable(env: CommandEnv): Promise<void> {
  await seedQuitDashboard(env);
  void ensureCigarettesCategoryAfterSync(env);
}
